import { SubroutineContext } from '@/context/SubroutineContext';
import { useApplication } from '@/hooks/useApplication';
import useDebouncedLocalStorage from '@/hooks/useDebouncedLocalStorage';
import { SubroutineSortType } from '@/types/subroutine';
import { findSubroutine } from '@/util/application';
import { useEffect } from 'react';
import { useLocalStorage } from 'usehooks-ts';

export const SubroutineProvider = ({ children }: { children: React.ReactNode }) => {
  const { application } = useApplication();

  const [selectedSubroutineId, setSelectedSubroutineId] = useDebouncedLocalStorage<number | undefined>(
    'currentSubroutine',
    undefined
  );
  const [sortSubroutinesBy, setSortSubroutinesBy] = useDebouncedLocalStorage<SubroutineSortType>(
    'sortSubroutines',
    'default'
  );
  const [subroutineHistory, setSubroutineHistory] = useLocalStorage<number[]>('subroutineHistory', []);

  const handleSubroutineChange = (newSubroutineId?: number, onBack?: boolean) => {
    if (selectedSubroutineId === newSubroutineId) return;

    if (selectedSubroutineId) {
      onBack
        ? setSubroutineHistory(subroutineHistory.slice(0, -1))
        : setSubroutineHistory((currentHistory) => [...currentHistory, selectedSubroutineId]);
    }

    setSelectedSubroutineId(newSubroutineId);
  };

  const clearSubroutineHistory = () => {
    setSubroutineHistory([]);
  };

  useEffect(() => {
    if (application && !findSubroutine(application.subroutines, selectedSubroutineId)) {
      setSelectedSubroutineId(application.subroutines[0] ? application.subroutines[0].id : undefined);
    }
  }, [application]);

  return (
    <SubroutineContext.Provider
      value={{
        subroutineHistory: subroutineHistory,
        clearSubroutineHistory: clearSubroutineHistory,
        sortSubroutinesBy: sortSubroutinesBy,
        setSortSubroutinesBy: setSortSubroutinesBy,
        selectedSubroutineId: selectedSubroutineId,
        setSelectedSubroutineId: handleSubroutineChange
      }}
    >
      {children}
    </SubroutineContext.Provider>
  );
};
